// The grouped list of results (listbox) of the flyout and of the results page. Focus never enters
// it: the search field holds it and points at the highlighted option (aria-activedescendant).
import { __css, mergeClasses, tokens } from '@fluentui/react-components';
import {
  ArrowRight16Regular,
  Briefcase20Regular,
  Building20Regular,
  CheckboxChecked20Regular,
  Chat20Regular,
  Document20Regular,
  Search20Regular,
} from '@fluentui/react-icons';
import type { SearchKind } from '@batiflow/contracts';
import { DirectionalIcon, brandTokens, layout } from '@batiflow/ui';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { SearchEntry, SearchHit, SearchListModel } from './model';

const KIND_ICONS: Readonly<Record<SearchKind, ReactElement>> = {
  projects: <Briefcase20Regular />,
  documents: <Document20Regular />,
  tasks: <CheckboxChecked20Regular />,
  clients: <Building20Regular />,
  messages: <Chat20Regular />,
};

const useStyles = __css({"list":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f47sw1k"},"group":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62"},"groupHeader":{"Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fjshy37","Be2twd7":"fy9rknc","Bg96gwp":"fwrc4pm","Bhrd7zp":"fl43uef","sj55zd":"f11d4kpn"},"option":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"f1xcgk68","qttt6h":"fsmx9qy","Bokbzmb":0,"Jyy4pa":0,"B1u1w3b":"f1hcf779","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f47sw1k","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","sj55zd":"f19n0e5","Bceei9c":"f1k6fduh"},"active":{"De3pzq":"f1bgiulx","sj55zd":"fulqd5u","Bhrd7zp":"fl43uef"},"icon":{"mc9l5x":"f22iagw","Bnnss6s":"fi64zpg","sj55zd":"f16muhyy"},"title":{"Bh6795r":"fqerorx","Bgto7am":"fuzgk9b","B68tc82":0,"Bmxbyg5":0,"Bpg54ce":"f1a3p1vp","ygn44y":"f1cmbuwj","Huce71":"fz5stix"},"code":{"Bnnss6s":"fi64zpg","Bahqtrf":"f1fedwem","Be2twd7":"fy9rknc","sj55zd":"f11d4kpn"},"channel":{"Bnnss6s":"fi64zpg","Be2twd7":"fy9rknc","sj55zd":"f11d4kpn"},"more":{"sj55zd":"fyind8e","Be2twd7":"fy9rknc"},"mark":{"De3pzq":"f16xkysk","sj55zd":"f1ym3bx4","Bhrd7zp":"fl43uef","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"fq9zq91"},"srOnly":{"qhf8xq":"f1euv43f","Bns2gvi":"fittdk8","st59p4":"f156r0dy","B68tc82":0,"Bmxbyg5":0,"Bpg54ce":"f1a3p1vp","Bgl5zvf":"f1wch0ki","Huce71":"fz5stix"}});

/**
 * The title of a hit with the matched segments emphasised (bold on a tinted background, plus a
 * hidden "match" cue for screen readers). The segments come from the API as plain text: nothing is
 * ever rendered as markup.
 */
export function HitTitle({ hit }: { hit: SearchHit }) {
  const { t } = useTranslation('search');
  const styles = useStyles();
  const segments = hit.highlight.length > 0 ? hit.highlight : [{ text: hit.title, match: false }];
  return (
    <>
      {segments.map((segment, index) =>
        segment.match ? (
          <mark key={index} className={styles.mark}>
            {segment.text}
            <span className={styles.srOnly}> ({t('hit.match')})</span>
          </mark>
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
    </>
  );
}

export interface ResultsListProps {
  model: SearchListModel;
  /** Prefix of the DOM ids (same as the one the model was built with). */
  idPrefix: string;
  listboxId: string;
  activeId: string | undefined;
  q: string;
  onHover: (id: string) => void;
  onOpen: (entry: SearchEntry) => void;
  className?: string;
}

export function ResultsList({
  model,
  idPrefix,
  listboxId,
  activeId,
  q,
  onHover,
  onOpen,
  className,
}: ResultsListProps) {
  const { t } = useTranslation('search');
  const styles = useStyles();

  const option = (entry: SearchEntry) => {
    const active = entry.id === activeId;
    const rowClass = mergeClasses(styles.option, active && styles.active);
    const handlers = {
      // The field keeps the focus: a press must not move it to the option.
      onMouseDown: (event: { preventDefault: () => void }) => {
        event.preventDefault();
      },
      onMouseMove: () => {
        if (!active) onHover(entry.id);
      },
      onClick: () => {
        onOpen(entry);
      },
    };
    if (entry.type === 'hit' && entry.hit) {
      const { hit } = entry;
      return (
        <div
          key={entry.id}
          id={entry.id}
          role="option"
          aria-selected={active}
          className={rowClass}
          {...handlers}
        >
          <span className={styles.icon} aria-hidden="true">
            {KIND_ICONS[hit.kind]}
          </span>
          <span className={styles.title} dir="auto">
            <HitTitle hit={hit} />
          </span>
          {hit.kind === 'messages' && hit.channelName ? (
            <bdi className={styles.channel}>{hit.channelName}</bdi>
          ) : null}
          {hit.code ? (
            <bdi className={styles.code} dir="ltr">
              {hit.code}
            </bdi>
          ) : null}
        </div>
      );
    }
    const label =
      entry.type === 'more' && entry.kind ? t(`seeMore.${entry.kind}`) : t('seeAll', { q });
    return (
      <div
        key={entry.id}
        id={entry.id}
        role="option"
        aria-selected={active}
        className={mergeClasses(rowClass, styles.more)}
        {...handlers}
      >
        <span className={styles.icon} aria-hidden="true">
          {entry.type === 'all' ? (
            <Search20Regular />
          ) : (
            <DirectionalIcon icon={ArrowRight16Regular} />
          )}
        </span>
        <span className={styles.title} dir="auto">
          {label}
        </span>
      </div>
    );
  };

  return (
    <div
      id={listboxId}
      role="listbox"
      aria-label={t('results.label')}
      className={mergeClasses(styles.list, className)}
    >
      {model.sections.map((section) => {
        const headerId = `${idPrefix}-h-${section.kind}`;
        return (
          <div key={section.kind} role="group" aria-labelledby={headerId} className={styles.group}>
            <div id={headerId} role="presentation" className={styles.groupHeader}>
              {t(`kinds.${section.kind}`)}
            </div>
            {section.entries.map(option)}
          </div>
        );
      })}
      {model.tail ? option(model.tail) : null}
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/search/results-list.griffel.css";